import { useEffect } from 'react';
import Navbar from '../../home/components/Navbar';
import Footer from '../../home/components/Footer';

export default function WideAnglePortfolio() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white text-gray-900 font-sans selection:bg-gray-200 selection:text-black">
      <title>Wide Angle | Alianza en Creación Audiovisual | Mood Agency</title>
      <meta name="description" content="Caso de éxito Wide Angle: Transformación digital para una empresa de postproducción audiovisual líder, utilizando WordPress, Elementor y TranslatePress." />
      <meta name="keywords" content="Wide Angle, Postproducción, Audiovisual, WordPress, Elementor, TranslatePress, Google Analytics, Cloudflare" />
      
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden bg-white">
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden">
          <div className="absolute w-[60vw] h-[60vw] bg-[radial-gradient(circle,var(--tw-gradient-stops))] from-blue-100 via-gray-100/50 to-transparent rounded-full blur-[80px] opacity-100 z-0"></div>
          <div className="w-[90vw] h-[90vw] border border-gray-200 rounded-full absolute opacity-40"></div>
          <div className="w-[70vw] h-[70vw] border border-gray-300 rounded-full absolute opacity-50"></div>
          <div className="w-[50vw] h-[50vw] border border-gray-300 rounded-full absolute opacity-60"></div>
        </div>
        
        <div className="relative z-10 container mx-auto px-6 text-center">
          <div className="mb-8 animate-fade-in-up">
            <span className="font-display text-[10px] tracking-[0.4em] text-gray-400 uppercase">
              CASO DE ÉXITO
            </span>
          </div>
          
          <h1 className="font-display text-[12vw] md:text-[8vw] leading-none font-bold uppercase tracking-tighter text-gray-900 mb-8 animate-fade-in-up delay-100">
            Wide Angle
          </h1>
          
          <p className="max-w-3xl mx-auto font-sans text-xl text-gray-500 font-light leading-relaxed animate-fade-in-up delay-200">
            Más que postproducción: aliados estratégicos en cada fase de la creación audiovisual.
          </p>
        </div>
      </section>

      {/* Key Metrics */}
      <section className="py-20 px-6 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-12">
          <div className="text-center group">
            <div className="font-display text-6xl md:text-7xl font-bold text-gray-900 mb-2 group-hover:scale-105 transition-transform duration-500">100%</div>
            <p className="font-sans text-sm text-gray-400 uppercase tracking-widest">Disponibilidad Audiovisual</p>
          </div>
          <div className="text-center group">
            <div className="font-display text-6xl md:text-7xl font-bold text-gray-900 mb-2 group-hover:scale-105 transition-transform duration-500">Global</div>
            <p className="font-sans text-sm text-gray-400 uppercase tracking-widest">Alcance de Proyectos</p>
          </div>
          <div className="text-center group">
            <div className="font-display text-6xl md:text-7xl font-bold text-gray-900 mb-2 group-hover:scale-105 transition-transform duration-500">Multi</div>
            <p className="font-sans text-sm text-gray-400 uppercase tracking-widest">Plataformas de Entrega</p>
          </div>
        </div>
      </section>

      {/* The Context */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold text-gray-900 mb-12 uppercase">El Contexto</h2>
          <p className="font-sans text-xl md:text-2xl text-gray-500 leading-relaxed font-light">
            Wide Angle buscaba consolidar su presencia digital para reflejar su posición como líderes en postproducción. Necesitaban una plataforma que no solo mostrara su portafolio, sino que comunicara su valor como aliados estratégicos en el proceso creativo.
          </p>
        </div>
      </section>

      {/* The Challenge */}
      <section className="py-32 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-20 items-center">
          <div>
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 mb-8 uppercase leading-tight">
              Desafío<br/>Audiovisual
            </h2>
            <p className="font-sans text-lg text-gray-500 leading-relaxed mb-12">
              El reto era crear una experiencia digital que estuviera a la altura de la calidad cinematográfica de sus proyectos, permitiendo una navegación fluida y una gestión de contenidos eficiente para su equipo interno.
            </p>
          </div>
          <div className="space-y-12">
            <div className="border-l border-gray-300 pl-8">
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2 uppercase">Identidad Digital</h3>
              <p className="font-sans text-gray-500">Desarrollo de una interfaz minimalista que resalte el contenido visual.</p>
            </div>
            <div className="border-l border-gray-300 pl-8">
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2 uppercase">Gestión Ágil</h3>
              <p className="font-sans text-gray-500">Implementación de WordPress y Elementor para autonomía total del equipo.</p>
            </div>
            <div className="border-l border-gray-300 pl-8">
              <h3 className="font-display text-xl font-bold text-gray-900 mb-2 uppercase">Escalabilidad</h3>
              <p className="font-sans text-gray-500">Arquitectura preparada para el crecimiento y la integración de nuevos mercados.</p>
            </div>
          </div>
        </div>
      </section>

      {/* The Solution */}
      <section className="py-32 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="mb-20">
            <h2 className="font-display text-4xl md:text-5xl font-bold text-gray-900 uppercase">La Solución</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-gray-200 border border-gray-200">
            <div className="bg-white p-12 hover:bg-gray-50 transition-colors duration-300">
              <h3 className="font-display text-2xl font-bold text-gray-900 mb-6 uppercase">Ecosistema WordPress</h3>
              <p className="font-sans text-gray-500 leading-relaxed">
                Desarrollamos un sitio robusto utilizando WordPress como núcleo, permitiendo una administración sencilla y segura de todos los activos digitales.
              </p>
            </div>
            <div className="bg-white p-12 hover:bg-gray-50 transition-colors duration-300">
              <h3 className="font-display text-2xl font-bold text-gray-900 mb-6 uppercase">Diseño con Elementor</h3>
              <p className="font-sans text-gray-500 leading-relaxed">
                Utilizamos Elementor Pro para crear una experiencia visual única, adaptada a la estética premium que define a Wide Angle.
              </p>
            </div>
            <div className="bg-white p-12 hover:bg-gray-50 transition-colors duration-300">
              <h3 className="font-display text-2xl font-bold text-gray-900 mb-6 uppercase">Infraestructura Pro</h3>
              <p className="font-sans text-gray-500 leading-relaxed">
                Integramos Cloudflare y Google Analytics para asegurar velocidad, seguridad y un seguimiento detallado del comportamiento del usuario.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact */}
      <section className="py-32 px-6 bg-black text-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="font-display text-3xl md:text-4xl font-bold mb-20 uppercase tracking-widest">Resultado</h2>
          
          <div className="max-w-4xl mx-auto">
            <p className="font-display text-2xl md:text-4xl leading-tight mb-12">
              "Una vitrina digital que proyecta profesionalismo y excelencia. Wide Angle ahora cuenta con la infraestructura necesaria para atraer a los mejores talentos y clientes de la industria."
            </p>
            <div className="w-20 h-1 bg-white mx-auto opacity-30"></div>
          </div>
        </div>
      </section>


      <Footer />
    </div>
  );
}
